import React, { useState, useEffect } from "react";
import { Icon } from "@amzn/meridian";
import refreshTokens from "@amzn/meridian-tokens/base/icon/refresh";
import "./ZoomControl.scss";

type ZoomControlProps = {
  videoRef: React.RefObject<HTMLVideoElement>;
  disabled?: boolean;
};

const ZoomControl = ({ videoRef, disabled = false }: ZoomControlProps) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 1, step: 0.1 });
  const [isChecking, setIsChecking] = useState(true);
  const [sliderValue, setSliderValue] = useState(50); // 0-100 scale, 50 = 1.0x

  // Check if zoom is supported and get zoom range
  useEffect(() => {
    const checkZoomSupport = async () => {
      try {
        if (!videoRef.current?.srcObject) {
          setIsChecking(false);
          return;
        }

        const stream = videoRef.current.srcObject as MediaStream;
        const videoTrack = stream.getVideoTracks()[0];
        
        if (!videoTrack) {
          setZoomSupported(false);
          setIsChecking(false);
          return;
        }

        // Check if zoom capability exists
        const capabilities = videoTrack.getCapabilities();
        
        if ('zoom' in capabilities && capabilities.zoom) {
          const zoomCap = capabilities.zoom as any;
          setZoomSupported(true);
          setZoomRange({
            min: zoomCap.min || 1,  // Use device's actual minimum
            max: Math.min(zoomCap.max || 10, 4.0),  // Cap at 4.0x for practical use
            step: 0.1,
          });
          
          // Get current zoom level and set slider position
          const settings = videoTrack.getSettings();
          if ('zoom' in settings && settings.zoom) {
            const currentZoom = settings.zoom as number;
            setZoomLevel(currentZoom);
            setSliderValue(zoomToSlider(currentZoom));
          } else {
            setSliderValue(50); // Default to center
          }
        } else {
          setZoomSupported(false);
        }
        
        setIsChecking(false);
      } catch (error) {
        console.warn("Failed to check zoom support:", error);
        setZoomSupported(false);
        setIsChecking(false);
      }
    };

    // Check after a short delay to ensure video stream is ready
    const timeout = setTimeout(checkZoomSupport, 500);
    
    return () => clearTimeout(timeout);
  }, [videoRef.current?.srcObject]);

  // Map slider position (0-100) to zoom level with 1.0x at center (50)
  const sliderToZoom = (sliderVal: number): number => {
    if (sliderVal <= 50) {
      // Left half: min → 1.0x
      const ratio = sliderVal / 50;
      return zoomRange.min + (1.0 - zoomRange.min) * ratio;
    } else {
      // Right half: 1.0x → max
      const ratio = (sliderVal - 50) / 50;
      return 1.0 + (zoomRange.max - 1.0) * ratio;
    }
  };

  // Map zoom level to slider position (0-100)
  const zoomToSlider = (zoom: number): number => {
    // Guard against invalid ranges
    if (zoomRange.min === zoomRange.max) return 50;
    
    if (zoom <= 1.0) {
      // Left half
      if (zoomRange.min >= 1.0) return 50; // No left half available
      const ratio = (zoom - zoomRange.min) / (1.0 - zoomRange.min);
      return ratio * 50;
    } else {
      // Right half
      if (zoomRange.max <= 1.0) return 50; // No right half available
      const ratio = (zoom - 1.0) / (zoomRange.max - 1.0);
      return 50 + ratio * 50;
    }
  };

  const applyZoom = async (newZoomLevel: number) => {
    try {
      if (!videoRef.current?.srcObject) {
        return;
      }

      const stream = videoRef.current.srcObject as MediaStream;
      const videoTrack = stream.getVideoTracks()[0];
      
      if (!videoTrack) {
        return;
      }

      // Clamp zoom level to valid range
      const clampedZoom = Math.max(zoomRange.min, Math.min(zoomRange.max, newZoomLevel));
      
      // Apply zoom constraint
      await videoTrack.applyConstraints({
        // @ts-ignore - zoom is not in TypeScript's MediaTrackConstraints type yet
        advanced: [{ zoom: clampedZoom }]
      });
      
      setZoomLevel(clampedZoom);
      setSliderValue(zoomToSlider(clampedZoom));
    } catch (error) {
      console.error("Failed to apply zoom:", error);
    }
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSliderValue = parseFloat(e.target.value);
    setSliderValue(newSliderValue);
    const newZoom = sliderToZoom(newSliderValue);
    applyZoom(newZoom);
  };

  const handleZoomIn = () => {
    const newSlider = Math.min(sliderValue + 10, 100);
    setSliderValue(newSlider);
    const newZoom = sliderToZoom(newSlider);
    applyZoom(newZoom);
  };

  const handleZoomOut = () => {
    const newSlider = Math.max(sliderValue - 10, 0);
    setSliderValue(newSlider);
    const newZoom = sliderToZoom(newSlider);
    applyZoom(newZoom);
  };

  const handleReset = () => {
    applyZoom(1.0);
  };

  // Don't render if zoom is not supported or still checking
  if (isChecking || !zoomSupported) {
    return null;
  }

  return (
    <div className="zoom-control-container">
      <div className="zoom-control-minimal">
        {zoomLevel !== 1.0 && (
          <button
            onClick={handleReset}
            disabled={disabled}
            className="zoom-reset-button"
            aria-label="Reset zoom"
          >
            <Icon tokens={refreshTokens} />
          </button>
        )}
        
        <button
          onClick={handleZoomOut}
          disabled={disabled || sliderValue <= 0}
          className="zoom-minus-button"
          aria-label="Zoom out"
        >
          −
        </button>
        
        <div className="zoom-slider-wrapper">
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={sliderValue}
            onChange={handleSliderChange}
            disabled={disabled}
            className="zoom-slider"
            aria-label="Zoom level"
          />
          <div className="zoom-level-display">
            {zoomLevel.toFixed(1)}x
          </div>
        </div>
        
        <button
          onClick={handleZoomIn}
          disabled={disabled || sliderValue >= 100}
          className="zoom-plus-button"
          aria-label="Zoom in"
        >
          +
        </button>
      </div>
    </div>
  );
};

export default ZoomControl;